import { YOU } from "@/api/memory-focus";
export const DENSE_LABELS = 8;
const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif';
export const LABEL_FONT = `500 11px ${FONT_STACK}`;
export const FOCUS_LABEL_FONT = `600 13px ${FONT_STACK}`;
export const FOCUS_HINT_FONT = `500 13px ${FONT_STACK}`;
export type TextMeasure = (text: string, font: string) => number;
export interface Box {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}
/** A projected node, as the label pass needs it. */
export interface LabelNode {
  key: string;
  x: number;
  y: number;
  z: number;
  half: number;
  vis: number;
  name: string;
  more: number;
  rank: number;
  lit: boolean;
}
export interface PlacedLabel {
  key: string;
  text: string;
  hint: string;
  x: number;
  y: number;
  nameWidth: number;
  big: boolean;
  forced: boolean;
  box: Box;
}
/** `pocket` is the pocket inside the frame, which labels keep clear of. */
export interface LabelInput {
  nodes: readonly LabelNode[];
  focusKey: string | null;
  hoverKey: string | null;
  dense: boolean;
  width: number;
  height: number;
  pocket: Box | null;
  measure: TextMeasure;
}
export const boxesOverlap = (a: Box, b: Box) =>
  a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
export function fitText(
  text: string,
  max: number,
  font: string,
  measure: TextMeasure,
): string {
  if (measure(text, font) <= max) return text;
  let end = text.length;
  while (end > 1 && measure(`${text.slice(0, end)}…`, font) > max) end -= 1;
  return `${text.slice(0, end).trimEnd()}…`;
}
/** Labels that fit (mockup `drawGlobeLabels`): the focus (13px) and the hovered thing first, then front things by rank
 * (all at 21 things, eight when dense, the lit ones when focused), each below, above, right or left of its avatar.
 * Labels never overlap each other; only the focus and hover labels may cover an avatar when nothing else fits. */
export function placeLabels({
  nodes,
  focusKey,
  hoverKey,
  dense,
  width,
  height,
  pocket,
  measure,
}: LabelInput): PlacedLabel[] {
  const obstacles: Box[] = pocket ? [pocket] : [];
  for (const node of nodes) {
    if ((node.z < 0 && node.key !== YOU) || node.vis < 0.5) continue;
    const r = node.half + 1;
    obstacles.push({
      x0: node.x - r,
      x1: node.x + r,
      y0: node.y - r,
      y1: node.y + r,
    });
  }
  const labels: PlacedLabel[] = [];
  const free = (box: Box, others: readonly Box[]) =>
    !others.some((other) => boxesOverlap(box, other));
  const place = (node: LabelNode, big: boolean, forced: boolean): boolean => {
    const nameFont = big ? FOCUS_LABEL_FONT : LABEL_FONT;
    const text = fitText(node.name, big ? 200 : 132, nameFont, measure);
    const nameWidth = measure(text, nameFont);
    const hint = node.more > 0 ? `+${node.more}` : "";
    const w =
      nameWidth +
      (hint ? measure(hint, big ? FOCUS_HINT_FONT : LABEL_FONT) + 4 : 0);
    const h = big ? 16 : 14;
    const boxAt = ([x, y]: readonly [number, number]): Box => ({
      x0: x - 3,
      x1: x + w + 3,
      y0: y - 1,
      y1: y + h + 1,
    });
    const spots = [
      [node.x - w / 2, node.y + node.half + 5],
      [node.x - w / 2, node.y - node.half - 5 - h],
      [node.x + node.half + 6, node.y - h / 2],
      [node.x - node.half - 6 - w, node.y - h / 2],
    ] as const;
    const labelBoxes = labels.map((label) => label.box);
    const fits = (box: Box) =>
      box.x0 >= 4 &&
      box.x1 <= width - 4 &&
      box.y0 >= 4 &&
      box.y1 <= height - 40;
    const spot =
      spots.find(
        (s) =>
          fits(boxAt(s)) &&
          free(boxAt(s), obstacles) &&
          free(boxAt(s), labelBoxes),
      ) ?? (forced ? spots.find((s) => free(boxAt(s), labelBoxes)) : undefined);
    if (!spot) return false;
    labels.push({
      key: node.key,
      text,
      hint,
      x: spot[0],
      y: spot[1],
      nameWidth,
      big,
      forced,
      box: boxAt(spot),
    });
    return true;
  };
  const byKey = new Map(nodes.map((node) => [node.key, node]));
  const focus = focusKey ? byKey.get(focusKey) : undefined;
  const hover =
    hoverKey && hoverKey !== focusKey ? byKey.get(hoverKey) : undefined;
  if (focus) place(focus, true, true);
  if (hover) place(hover, false, true);
  const minZ = focusKey ? -0.05 : dense ? 0.15 : 0.02;
  const candidates = nodes
    .filter(
      (node) =>
        node !== focus &&
        node !== hover &&
        (!focusKey || node.lit) &&
        (node.key === YOU || node.z > minZ),
    )
    .sort((a, b) => a.rank - b.rank);
  let budget = focusKey || !dense ? Infinity : DENSE_LABELS;
  for (const node of candidates) {
    if (budget <= 0) break;
    if (place(node, false, false)) budget -= 1;
  }
  return labels;
}
